import {
  BadgeCheck,
  Copy,
  SlidersHorizontal,
  Trash2,
  X,
} from "lucide-react";

import { EmphasisRulesEditor } from "./EmphasisRulesEditor";
import type {
  ManuscriptTemplate,
  ParagraphStyleRule,
} from "../domain/templates";

type TemplateDrawerProps = {
  open: boolean;
  template: ManuscriptTemplate;
  onClose: () => void;
  onChange: (template: ManuscriptTemplate) => void;
  onDuplicate: () => void;
  onDelete: () => void;
  custom: boolean;
};

function NumberField({
  label,
  value,
  min,
  max,
  step = 1,
  unit,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  unit: string;
  onChange: (value: number) => void;
}) {
  return (
    <label className="number-field">
      <span>{label}</span>
      <div>
        <input
          aria-label={label}
          autoComplete="off"
          max={max}
          min={min}
          name={label}
          onChange={(event) => onChange(Number(event.target.value))}
          step={step}
          type="number"
          value={value}
        />
        <small>{unit}</small>
      </div>
    </label>
  );
}

function CompactParagraphRule({
  label,
  rule,
  onChange,
}: {
  label: string;
  rule: ParagraphStyleRule;
  onChange: (patch: Partial<ParagraphStyleRule>) => void;
}) {
  return (
    <fieldset className="drawer-paragraph-rule">
      <legend>{label}</legend>
      <div className="number-grid">
        <NumberField
          label="行距"
          max={3}
          min={1}
          onChange={(lineSpacing) => onChange({ lineSpacing })}
          step={0.1}
          unit="倍"
          value={rule.lineSpacing}
        />
        <NumberField
          label="首行缩进"
          max={20}
          min={0}
          onChange={(firstLineIndentMm) => onChange({ firstLineIndentMm })}
          step={0.1}
          unit="mm"
          value={rule.firstLineIndentMm}
        />
        <NumberField
          label="段前"
          max={36}
          min={0}
          onChange={(spaceBeforePt) => onChange({ spaceBeforePt })}
          step={0.5}
          unit="pt"
          value={rule.spaceBeforePt}
        />
        <NumberField
          label="段后"
          max={36}
          min={0}
          onChange={(spaceAfterPt) => onChange({ spaceAfterPt })}
          step={0.5}
          unit="pt"
          value={rule.spaceAfterPt}
        />
      </div>
      <label className="wide-field">
        <span>对齐</span>
        <select
          onChange={(event) =>
            onChange({
              alignment: event.target.value as ParagraphStyleRule["alignment"],
            })
          }
          value={rule.alignment}
        >
          <option value="left">左对齐</option>
          <option value="center">居中</option>
          <option value="right">右对齐</option>
          <option value="justify">两端对齐</option>
        </select>
      </label>
    </fieldset>
  );
}

export function TemplateDrawer({
  open,
  template,
  onClose,
  onChange,
  onDuplicate,
  onDelete,
  custom,
}: TemplateDrawerProps) {
  if (!open) return null;

  const updateTypography = (
    patch: Partial<ManuscriptTemplate["typography"]>,
  ) => onChange({ ...template, typography: { ...template.typography, ...patch } });
  const updatePage = (patch: Partial<ManuscriptTemplate["page"]>) =>
    onChange({ ...template, page: { ...template.page, ...patch } });
  const updateMargin = (
    side: keyof ManuscriptTemplate["page"]["marginsMm"],
    value: number,
  ) =>
    updatePage({
      marginsMm: { ...template.page.marginsMm, [side]: value },
    });
  const updateParagraph = (
    patch: Partial<ManuscriptTemplate["paragraph"]>,
  ) => onChange({ ...template, paragraph: { ...template.paragraph, ...patch } });
  const updateReferences = (
    patch: Partial<ManuscriptTemplate["references"]>,
  ) => onChange({ ...template, references: { ...template.references, ...patch } });
  const updateEmphasisRules = (
    emphasisRules: ManuscriptTemplate["emphasisRules"],
  ) => onChange({ ...template, emphasisRules });
  const updateBlockStyle = (
    key: keyof ManuscriptTemplate["blockStyles"],
    patch: Partial<ParagraphStyleRule>,
  ) =>
    onChange({
      ...template,
      blockStyles: {
        ...template.blockStyles,
        [key]: { ...template.blockStyles[key], ...patch },
      },
    });

  return (
    <div className="drawer-backdrop" role="presentation" onMouseDown={onClose}>
      <aside
        aria-label="模板设计器"
        className="template-drawer"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header>
          <div className="drawer-title-icon">
            <SlidersHorizontal size={19} />
          </div>
          <div>
            <span className="eyebrow">STYLE PROFILE</span>
            <h2>模板设计器</h2>
          </div>
          <button aria-label="关闭模板设计器" onClick={onClose} type="button">
            <X size={19} />
          </button>
        </header>

        <div className="template-identity">
          <div>
            <span className={`template-status ${template.status}`}>
              {template.status === "verified" ? <BadgeCheck size={13} /> : null}
              {template.status === "verified"
                ? "已核验"
                : template.status === "baseline"
                  ? "通用基线"
                  : "自定义草稿"}
            </span>
            <h3>{template.name}</h3>
            <p>{template.description}</p>
          </div>
          <div className="template-identity-actions">
            <button className="duplicate-template" onClick={onDuplicate} type="button">
              <Copy size={14} /> 复制为自定义
            </button>
            {custom ? (
              <button className="delete-template" onClick={onDelete} type="button">
                <Trash2 size={14} /> 删除
              </button>
            ) : null}
          </div>
        </div>

        <div className="drawer-scroll">
          <section className="drawer-section">
            <div className="drawer-section-heading">
              <span>00</span>
              <div>
                <h3>模板身份</h3>
                <p>外部载入与自定义模板始终先按草稿处理。</p>
              </div>
            </div>
            <label className="wide-field">
              <span>模板名称</span>
              <input
                maxLength={500}
                onChange={(event) =>
                  onChange({ ...template, name: event.target.value })
                }
                value={template.name}
              />
            </label>
            <label className="wide-field">
              <span>模板类型</span>
              <select
                onChange={(event) =>
                  onChange({
                    ...template,
                    authority: event.target.value as ManuscriptTemplate["authority"],
                  })
                }
                value={template.authority}
              >
                <option value="generic">通用</option>
                <option value="journal">期刊</option>
                <option value="school">学校</option>
              </select>
            </label>
            <label className="wide-field">
              <span>官方来源</span>
              <input
                maxLength={3000}
                onChange={(event) =>
                  onChange({ ...template, sourceUrl: event.target.value })
                }
                placeholder="https://journal.example/guide…"
                value={template.sourceUrl}
              />
            </label>
            <label className="wide-field">
              <span>核验日期</span>
              <input
                onChange={(event) =>
                  onChange({ ...template, verifiedOn: event.target.value })
                }
                type="date"
                value={template.verifiedOn}
              />
            </label>
            <label className="wide-field wide-field-stack">
              <span>用途说明</span>
              <textarea
                maxLength={3000}
                onChange={(event) =>
                  onChange({ ...template, description: event.target.value })
                }
                rows={3}
                value={template.description}
              />
            </label>
          </section>

          <section className="drawer-section">
            <div className="drawer-section-heading">
              <span>01</span>
              <div>
                <h3>字体与字号</h3>
                <p>所有可见文字固定为黑色。</p>
              </div>
            </div>
            <label className="wide-field">
              <span>西文字体</span>
              <input
                onChange={(event) =>
                  updateTypography({ latinFont: event.target.value })
                }
                value={template.typography.latinFont}
              />
            </label>
            <label className="wide-field">
              <span>东亚字体</span>
              <input
                onChange={(event) =>
                  updateTypography({ eastAsiaFont: event.target.value })
                }
                value={template.typography.eastAsiaFont}
              />
            </label>
            <div className="number-grid">
              <NumberField
                label="正文"
                max={18}
                min={8}
                onChange={(bodySizePt) => updateTypography({ bodySizePt })}
                unit="pt"
                value={template.typography.bodySizePt}
              />
              <NumberField
                label="题名"
                max={32}
                min={12}
                onChange={(titleSizePt) => updateTypography({ titleSizePt })}
                unit="pt"
                value={template.typography.titleSizePt}
              />
              <NumberField
                label="H1"
                max={24}
                min={9}
                onChange={(h1SizePt) => updateTypography({ h1SizePt })}
                unit="pt"
                value={template.typography.h1SizePt}
              />
              <NumberField
                label="H2"
                max={22}
                min={8}
                onChange={(h2SizePt) => updateTypography({ h2SizePt })}
                unit="pt"
                value={template.typography.h2SizePt}
              />
              <NumberField
                label="H3"
                max={20}
                min={8}
                onChange={(h3SizePt) => updateTypography({ h3SizePt })}
                unit="pt"
                value={template.typography.h3SizePt}
              />
              <NumberField
                label="表题/表注"
                max={16}
                min={8}
                onChange={(captionSizePt) => updateTypography({ captionSizePt })}
                unit="pt"
                value={template.typography.captionSizePt}
              />
              <NumberField
                label="图注字号"
                max={16}
                min={8}
                onChange={(figureCaptionSizePt) =>
                  updateTypography({ figureCaptionSizePt })
                }
                unit="pt"
                value={template.typography.figureCaptionSizePt}
              />
            </div>
          </section>

          <section className="drawer-section">
            <div className="drawer-section-heading">
              <span>02</span>
              <div>
                <h3>页面与段落</h3>
                <p>数值会同步到预览和 Word。</p>
              </div>
            </div>
            <div className="number-grid">
              {(
                [
                  ["top", "上边距"],
                  ["right", "右边距"],
                  ["bottom", "下边距"],
                  ["left", "左边距"],
                ] as const
              ).map(([side, label]) => (
                <NumberField
                  key={side}
                  label={label}
                  max={60}
                  min={5}
                  onChange={(value) => updateMargin(side, value)}
                  step={0.1}
                  unit="mm"
                  value={template.page.marginsMm[side]}
                />
              ))}
              <NumberField
                label="行距"
                max={3}
                min={1}
                onChange={(lineSpacing) => updateParagraph({ lineSpacing })}
                step={0.1}
                unit="倍"
                value={template.paragraph.lineSpacing}
              />
              <NumberField
                label="首行缩进"
                max={20}
                min={0}
                onChange={(firstLineIndentMm) =>
                  updateParagraph({ firstLineIndentMm })
                }
                step={0.1}
                unit="mm"
                value={template.paragraph.firstLineIndentMm}
              />
              <NumberField
                label="段前间距"
                max={36}
                min={0}
                onChange={(spaceBeforePt) =>
                  updateParagraph({ spaceBeforePt })
                }
                step={0.5}
                unit="pt"
                value={template.paragraph.spaceBeforePt}
              />
              <NumberField
                label="段后间距"
                max={36}
                min={0}
                onChange={(spaceAfterPt) =>
                  updateParagraph({ spaceAfterPt })
                }
                step={0.5}
                unit="pt"
                value={template.paragraph.spaceAfterPt}
              />
            </div>
            <div className="drawer-options">
              <label>
                <span>纸张</span>
                <select
                  onChange={(event) =>
                    updatePage({ size: event.target.value as "A4" | "Letter" })
                  }
                  value={template.page.size}
                >
                  <option value="A4">A4</option>
                  <option value="Letter">Letter</option>
                </select>
              </label>
              <label>
                <span>正文对齐</span>
                <select
                  onChange={(event) =>
                    updateParagraph({
                      alignment: event.target.value as "left" | "justify",
                    })
                  }
                  value={template.paragraph.alignment}
                >
                  <option value="left">左对齐</option>
                  <option value="justify">两端对齐</option>
                </select>
              </label>
            </div>
          </section>

          <section className="drawer-section">
            <div className="drawer-section-heading">
              <span>03</span>
              <div>
                <h3>参考文献段落</h3>
                <p>编号和悬挂缩进独立于正文首行缩进。</p>
              </div>
            </div>
            <div className="number-grid">
              <NumberField
                label="悬挂缩进"
                max={20}
                min={0}
                onChange={(hangingIndentMm) =>
                  updateReferences({ hangingIndentMm })
                }
                step={0.1}
                unit="mm"
                value={template.references.hangingIndentMm}
              />
              <NumberField
                label="行距"
                max={3}
                min={1}
                onChange={(lineSpacing) => updateReferences({ lineSpacing })}
                step={0.1}
                unit="倍"
                value={template.references.lineSpacing}
              />
              <NumberField
                label="段后间距"
                max={36}
                min={0}
                onChange={(spaceAfterPt) => updateReferences({ spaceAfterPt })}
                step={0.5}
                unit="pt"
                value={template.references.spaceAfterPt}
              />
            </div>
            <div className="drawer-options">
              <label>
                <span>参考文献编号</span>
                <select
                  onChange={(event) =>
                    updateReferences({
                      numbering: event.target.value as "preserve" | "numeric",
                    })
                  }
                  value={template.references.numbering}
                >
                  <option value="preserve">保留原文</option>
                  <option value="numeric">按顺序自动编号</option>
                </select>
              </label>
            </div>
          </section>

          <section className="drawer-section compact-options">
            <div className="drawer-section-heading">
              <span>04</span>
              <div>
                <h3>文档机制</h3>
                <p>按目标要求开启，不做出版版式猜测。</p>
              </div>
            </div>
            <label>
              <span>标题编号</span>
              <select
                onChange={(event) =>
                  onChange({
                    ...template,
                    numbering: event.target.value as "none" | "decimal",
                  })
                }
                value={template.numbering}
              >
                <option value="none">不编号</option>
                <option value="decimal">1 / 1.1 / 1.1.1</option>
              </select>
            </label>
            <label className="toggle-row">
              <span>页码</span>
              <input
                checked={template.page.pageNumbers}
                onChange={(event) =>
                  updatePage({ pageNumbers: event.target.checked })
                }
                type="checkbox"
              />
            </label>
            <label className="toggle-row">
              <span>行号</span>
              <input
                checked={template.page.lineNumbers}
                onChange={(event) =>
                  updatePage({ lineNumbers: event.target.checked })
                }
                type="checkbox"
              />
            </label>
            {template.page.lineNumbers ? (
              <div className="drawer-line-number-settings">
                <label className="wide-field">
                  <span>行号方式</span>
                  <select
                    onChange={(event) =>
                      updatePage({
                        lineNumberMode: event.target
                          .value as ManuscriptTemplate["page"]["lineNumberMode"],
                      })
                    }
                    value={template.page.lineNumberMode}
                  >
                    <option value="continuous">整篇连续</option>
                    <option value="restartPage">每页重启</option>
                    <option value="restartSection">每节重启</option>
                  </select>
                </label>
                <div className="number-grid">
                  <NumberField
                    label="起始值"
                    max={9999}
                    min={1}
                    onChange={(lineNumberStart) => updatePage({ lineNumberStart })}
                    unit=""
                    value={template.page.lineNumberStart}
                  />
                  <NumberField
                    label="显示间隔"
                    max={20}
                    min={1}
                    onChange={(lineNumberInterval) =>
                      updatePage({ lineNumberInterval })
                    }
                    unit="行"
                    value={template.page.lineNumberInterval}
                  />
                </div>
              </div>
            ) : null}
          </section>

          <section className="drawer-section">
            <div className="drawer-section-heading">
              <span>05</span>
              <div>
                <h3>摘要与标题段落</h3>
                <p>摘要、H1、H2、H3 的段落参数分别保存。</p>
              </div>
            </div>
            <div className="drawer-paragraph-rules">
              <CompactParagraphRule
                label="摘要"
                onChange={(patch) => updateBlockStyle("abstract", patch)}
                rule={template.blockStyles.abstract}
              />
              <CompactParagraphRule
                label="一级标题"
                onChange={(patch) => updateBlockStyle("h1", patch)}
                rule={template.blockStyles.h1}
              />
              <CompactParagraphRule
                label="二级标题"
                onChange={(patch) => updateBlockStyle("h2", patch)}
                rule={template.blockStyles.h2}
              />
              <CompactParagraphRule
                label="三级标题"
                onChange={(patch) => updateBlockStyle("h3", patch)}
                rule={template.blockStyles.h3}
              />
              <CompactParagraphRule
                label="图注"
                onChange={(patch) => updateBlockStyle("figureCaption", patch)}
                rule={template.blockStyles.figureCaption}
              />
            </div>
          </section>

          <section className="drawer-section">
            <div className="drawer-section-heading">
              <span>06</span>
              <div>
                <h3>文字强调规则</h3>
                <p>按字面匹配稿件内容，并同步到预览和 Word。</p>
              </div>
            </div>
            <EmphasisRulesEditor
              compact
              onChange={updateEmphasisRules}
              rules={template.emphasisRules}
            />
          </section>
        </div>
      </aside>
    </div>
  );
}
